import { ToWords } from "to-words/en-IN";
import ReceiptPrint from "@/components/receipt-layout";
import { useHardcopyBorders } from "@/theme/useHardcopyBorders";
import { formatRupees } from "../shared/fee-format";

export type ReceiptRecord = {
  id: string;
  receiptNo: string;
  receiptDate: string;
  academicYearName: string;
  studentName: string;
  admissionNo: number;
  itsId: string;
  className: string | null;
  totalAmount: number;
  paymentMode: "CASH" | "BANK_TRANSFER" | "CHEQUE" | "UPI";
  paymentReference: string | null;
  remarks: string | null;
  voided: boolean;
  voidReason: string | null;
  /** Only on the full receipt; list rows carry just a count. */
  allocations?: Array<{ id: string; feeHeadName: string; periodLabel: string; amount: number }>;
};

const MODE_LABEL: Record<ReceiptRecord["paymentMode"], string> = {
  CASH: "Cash",
  BANK_TRANSFER: "Bank transfer",
  CHEQUE: "Cheque",
  UPI: "UPI",
};

export const PRINT_ROOT_ID = "fee-receipt-print";

// Print only the receipt (not the app's menu and toolbars). A5 landscape suits a half-page receipt.
const PRINT_CSS = `
@page { size: A5 landscape; margin: 8mm; }
@media print {
  body * { visibility: hidden !important; }
  #${PRINT_ROOT_ID}, #${PRINT_ROOT_ID} * { visibility: visible !important; }
  #${PRINT_ROOT_ID} { position: absolute; left: 0; top: 0; width: 100%; }
}
`;

/** The receipt as it prints, with the tenant's branding. A voided receipt is stamped VOID. */
export function FeeReceiptSheet({ receipt }: { receipt: ReceiptRecord }) {
  const { solid1, solid1Soft } = useHardcopyBorders();
  const words = new ToWords().convert(receipt.totalAmount);

  return (
    <div id={PRINT_ROOT_ID} style={{ position: "relative", maxWidth: 820 }}>
      <style>{PRINT_CSS}</style>
      <ReceiptPrint
        subTitle="Madrasa fee receipt"
        footerNotes={["This is a computer generated receipt and does not require a signature."]}
      >
        <div style={{ padding: 16, position: "relative" }}>
          {receipt.voided && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              <span
                style={{
                  transform: "rotate(-18deg)",
                  fontSize: 96,
                  fontWeight: 800,
                  letterSpacing: 8,
                  color: "rgba(200,0,0,0.28)",
                  border: "6px solid rgba(200,0,0,0.28)",
                  padding: "0 24px",
                }}
              >
                VOID
              </span>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <strong>Receipt no:</strong> {receipt.receiptNo}
            </div>
            <div>
              <strong>Date:</strong> {receipt.receiptDate}
            </div>
          </div>

          <div style={{ borderBottom: solid1, paddingBottom: 8, marginBottom: 8 }}>
            <div>
              <strong>Received from:</strong> {receipt.studentName}
            </div>
            <div style={{ fontSize: 13 }}>
              Admission no. {receipt.admissionNo} · ITS {receipt.itsId}
              {receipt.className ? ` · ${receipt.className}` : ""} · {receipt.academicYearName}
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", borderBottom: solid1, padding: "4px 6px" }}>Fee</th>
                <th style={{ textAlign: "left", borderBottom: solid1, padding: "4px 6px" }}>
                  Period
                </th>
                <th style={{ textAlign: "right", borderBottom: solid1, padding: "4px 6px" }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {(receipt.allocations ?? []).map((a) => (
                <tr key={a.id}>
                  <td style={{ borderBottom: solid1Soft, padding: "4px 6px" }}>{a.feeHeadName}</td>
                  <td style={{ borderBottom: solid1Soft, padding: "4px 6px" }}>{a.periodLabel}</td>
                  <td style={{ borderBottom: solid1Soft, padding: "4px 6px", textAlign: "right" }}>
                    {formatRupees(a.amount)}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={2} style={{ padding: "6px", textAlign: "right" }}>
                  <strong>Total</strong>
                </td>
                <td style={{ padding: "6px", textAlign: "right" }}>
                  <strong>{formatRupees(receipt.totalAmount)}</strong>
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: 8, fontSize: 13 }}>
            <div>
              <strong>In words:</strong> {words} only
            </div>
            <div>
              <strong>Paid by:</strong> {MODE_LABEL[receipt.paymentMode]}
              {receipt.paymentReference ? ` (ref. ${receipt.paymentReference})` : ""}
            </div>
            {receipt.remarks && (
              <div>
                <strong>Remarks:</strong> {receipt.remarks}
              </div>
            )}
            {receipt.voided && (
              <div style={{ color: "#b00020" }}>
                <strong>This receipt has been voided.</strong>
                {receipt.voidReason ? ` Reason: ${receipt.voidReason}` : ""}
              </div>
            )}
          </div>
        </div>
      </ReceiptPrint>
    </div>
  );
}
